import type {
  CompositionLocalisation,
  CompositionType,
} from "@/types/mel-components";

export function validateCompositionsForSave(
  compositions: Array<{
    compositionId: number;
    locationId: number;
    percentage: number;
  }>,
  type: CompositionType,
  locations: CompositionLocalisation[],
) {
  const totalsByLocation = new Map<number, number>();
  for (const composition of compositions) {
    totalsByLocation.set(
      composition.locationId,
      (totalsByLocation.get(composition.locationId) || 0) +
        composition.percentage,
    );
  }
  const valid =
    compositions.length > 0 &&
    compositions.every((composition) => composition.percentage > 0) &&
    Array.from(totalsByLocation.values()).every(
      (total) => Math.abs(total - 100) <= 0.01,
    ) &&
    (type !== "chaussures" ||
      locations.every((location) =>
        compositions.some(
          (composition) =>
            composition.locationId === location.id_composition_localisation,
        ),
      ));
  return {
    valid,
    message:
      type === "chaussures"
        ? "Sélectionnez une composition pour chaque zone de la chaussure."
        : "Chaque emplacement de composition doit totaliser exactement 100 %.",
  };
}

export function parsePackQuantities(value: string | number | undefined | null) {
  return String(value || "")
    .split(",")
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((quantity) => !Number.isNaN(quantity));
}

/** Poids unitaire max (mode / chaussures) — au-delà, saisie bloquée (frais de port). */
export const MAX_MEL_UNIT_WEIGHT_KG = 5;
export const WARN_MEL_UNIT_WEIGHT_KG = 2;
export const MAX_MEL_UNIT_WEIGHT_ERROR = `Le poids unitaire ne peut pas dépasser ${MAX_MEL_UNIT_WEIGHT_KG} kg.`;

export function parsePositivePrice(value: string): number | undefined {
  const parsed = Number(String(value).trim().replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.round(parsed * 100) / 100;
}

export function parsePositiveWeight(value: string): number | undefined {
  const parsed = Number(String(value).trim().replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  // Arrondi au supérieur à 1 décimale (ex. 4.53592 → 4.6)
  return Math.ceil(parsed * 10 - 1e-9) / 10;
}

export function isMelUnitWeightAllowed(weight: number): boolean {
  return (
    Number.isFinite(weight) && weight > 0 && weight <= MAX_MEL_UNIT_WEIGHT_KG
  );
}

/** Retourne un message d'erreur si le poids unitaire est invalide, sinon null. */
export function getMelUnitWeightError(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "Le poids est obligatoire et doit être supérieur à 0.";
  }
  const parsed =
    typeof value === "number"
      ? value
      : Number(String(value).trim().replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return "Le poids est obligatoire et doit être supérieur à 0.";
  }
  const ceiled = Math.ceil(parsed * 10 - 1e-9) / 10;
  if (ceiled > MAX_MEL_UNIT_WEIGHT_KG) {
    return MAX_MEL_UNIT_WEIGHT_ERROR;
  }
  return null;
}
